import { pageUrl } from '@/constants/url'
import { createRouteForLink } from '@/utils/helpers/urlHelper'
import { useScreenWidth } from '@/hooks/commons/useScreenWidth'
import { usePermissions } from '@/hooks/commons/usePermissions'
import { useLeaveEnabledGuard } from '@/hooks/leave/useLeaveEnabledGuard'
import { leavePermissions } from '@/constants/permissions'
import {
  AuditOutlined,
  CalendarOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { Breadcrumb, Menu, MenuProps, Typography } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'
import classNames from 'classnames'

const { Title, Paragraph } = Typography

const MenuLink = ({ label, url }: { label: string; url: string }) => {
  return <Link to={url}>{label}</Link>
}

export const LeaveLayout = () => {
  const location = useLocation()
  const { isDesktop } = useScreenWidth()
  const { t } = useTranslation('leave')
  const { t: ts } = useTranslation('settings')
  const { t: tg } = useTranslation()
  const { havePermissionAccess } = usePermissions()
  const { leaveEnabled } = useLeaveEnabledGuard()

  const isConfig = location.pathname === pageUrl.leaveConfig
  const isCalendar = location.pathname === pageUrl.leaveCalendar
  const breadcrumbItems = [
    {
      title: (
        <Link to={createRouteForLink(pageUrl.home)}>{tg('common.home')}</Link>
      ),
    },
    {
      title: <Link to={pageUrl.settings}>{tg('menu.settings')}</Link>,
    },
    {
      title: tg('menu.leave'),
    },
  ]

  const items = useMemo(() => {
    const menuItems: MenuProps['items'] = []
    if (havePermissionAccess({ permission: leavePermissions.read })) {
      menuItems.push({
        label: (
          <MenuLink label={t('layout.calendar')} url={pageUrl.leaveCalendar} />
        ),
        key: pageUrl.leaveCalendar,
        icon: <CalendarOutlined />,
      })
    }
    if (havePermissionAccess({ permission: leavePermissions.adjust })) {
      menuItems.push({
        label: (
          <MenuLink label={t('layout.config')} url={pageUrl.leaveConfig} />
        ),
        key: pageUrl.leaveConfig,
        icon: <SettingOutlined />,
      })
    }
    if (havePermissionAccess({ permission: leavePermissions.approve })) {
      menuItems.push({
        label: (
          <MenuLink
            label={t('layout.approvals')}
            url={pageUrl.leaveApprovals}
          />
        ),
        key: pageUrl.leaveApprovals,
        icon: <AuditOutlined />,
      })
    }
    return menuItems
  }, [t, havePermissionAccess])

  if (!leaveEnabled) return null

  return (
    <div>
      <Breadcrumb items={breadcrumbItems} />
      <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>
        {isConfig
          ? ts('settings.leave.title')
          : isCalendar
            ? t('calendar.title')
            : t('approvals.title')}
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {isConfig
          ? ts('settings.leave.description')
          : isCalendar
            ? t('calendar.desc')
            : t('approvals.desc')}
      </Paragraph>
      <div
        className={classNames('flex  gap-4 mt-4', {
          'flex-col': !isDesktop,
        })}
      >
        <div
          className={classNames({
            'w-[250px]': isDesktop,
            'w-full': !isDesktop,
          })}
        >
          <Menu
            className="rounded"
            items={items}
            selectedKeys={[location.pathname]}
            mode={!isDesktop ? 'horizontal' : 'inline'}
          />
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
